import { computeUsageDefaults } from "./usageForm";

// 是否已開通（activation_state 可能是 boolean 或 0/1；列表資料未帶此欄位則視為已開通）
const isActivated = (s) =>
  s?.activation_state === true ||
  s?.activation_state === 1 ||
  s?.activation_state === undefined ||
  s?.activation_state === null;

// 是否曾經真的被開通過：start_at 為真實日期才算（null 或「尚未開通」字串皆視為從沒開通）。
const wasEverActivated = (s) => {
  const t = s?.start_at;
  return !!t && !Number.isNaN(new Date(t).getTime());
};

// 單一服務的狀態徽章（由後端 subscription_status 決定，前端不另做日期判斷）。
export const getStatusMeta = (data) => {
  if (!isActivated(data)) {
    // 停用狀態：曾開通過 → 已停用；從來沒開通過 → 未啟用。
    return wasEverActivated(data)
      ? { label: "已停用", cls: "off" }
      : { label: "未啟用", cls: "off" };
  }
  const st = data?.subscription_status;
  if (st === "on_trial") return { label: "試用中", cls: "trial" };
  if (st === "subscribing")
    return {
      label: data?.software_services_id === 8 ? "已啟用" : "訂閱中",
      cls: "on",
    };
  if (st === "no_purchase") return { label: "未購買", cls: "off" };
  if (st === "no_renew") return { label: "未續訂", cls: "no_renew" };
  return { label: "已停用", cls: "off" };
};

/**
 * 服務卡（PlanCard）訂閱式開通表單的初值、驗證與變更判斷。
 *
 * 未來企業端一律視為訂閱：開通不再受「無到期日」限制，switch 只是草稿開關，
 * 填完「席位上限 + 使用期間」按「儲存變更」才送出（走 activate_trial upsert）。
 */

// 由服務資料建表單初值；未訂閱（無日期）時以預設起訖日帶入。
export const toPlanForm = (data = {}, today) => {
  const defaults = computeUsageDefaults(today);
  return {
    activate: !!data.activation_state,
    seat: Number(data.users_count_upper_limit) || 0,
    start_date: data.start_date || defaults.start_date,
    expiration_date: data.expiration_date || defaults.expiration_date,
    is_trial: !!data.is_trial,
    // 無席位服務（資料中心 / 任務自動化）的容量上限（GB）。來源＝usage-api 的
    // enterprise_service_quotas（由 EnterpriseList 開窗時 merge 進 data.quota_gb）。
    // 非無席位服務不會用到此欄。空字串＝尚未帶入（避免顯示成 0）。
    quota_gb: data.quota_gb == null ? "" : Number(data.quota_gb),
  };
};

const isNonNegativeInteger = (v) => Number.isInteger(v) && v >= 0;

/**
 * 儲存前驗證，回傳 { ok, errors }。
 *  - 席位必須為非負整數
 *  - 啟用時：席位 > 0、使用期間必填、結束日不早於開始日
 *  - skipSeat：無席位設定的服務（見 NO_SEAT_SERVICE_IDS）跳過席位相關驗證
 */
export const validatePlanCard = (form = {}, { skipSeat = false } = {}) => {
  const { activate, seat, start_date, expiration_date } = form;
  const errors = {};

  if (!skipSeat && !isNonNegativeInteger(seat)) {
    errors.seat = "席位上限需為 0 或正整數";
  }

  if (
    start_date &&
    expiration_date &&
    new Date(expiration_date) < new Date(start_date)
  ) {
    errors.expiration_date = "結束日期不可早於開始日期";
  }

  if (activate) {
    if (!start_date) errors.start_date = "請填寫開始日期";
    if (!expiration_date) errors.expiration_date = "請填寫結束日期";
    if (!skipSeat && !errors.seat && seat < 1) {
      errors.seat = "啟用時席位上限需大於 0";
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
};

// 表單是否有變動（與載入時的初始值相比）。
const FIELDS = ["activate", "seat", "start_date", "expiration_date", "is_trial"];
export const isPlanCardDirty = (form = {}, initial = {}) =>
  FIELDS.some((k) => form[k] !== initial[k]);

/* ────────────────────────────────────────────────────────────
 * 席位制服務：管理方案彈窗以「每面額分配人數」清單取代單一席位上限欄位。
 * 面額方案來源為 menu 的 avaliable_plans（每筆有 id 與 name，如「NT$200」），
 * 分配人數以 plan_allocations:[{ plan_id, users_count }] 回填與送出。
 * ──────────────────────────────────────────────────────────── */
export const SEAT_SERVICE_IDS = [9];
export const isSeatService = (id) => SEAT_SERVICE_IDS.includes(id);

/* ────────────────────────────────────────────────────────────
 * 無席位設定的服務（企業資料中心 data-center／自動化任務 automation）：
 * 這兩個服務為年費、按企業計價，不做人數控管。
 * 前端隱藏席位欄位，送出時帶 unlimited_seats: true，
 * 後端把 users_count_upper_limit 存成 null＝不設上限。
 *
 * 為什麼是 null 而不是 1 或 -1：
 *  - null 是後端既有的「無上限」哨兵值，getMaximumNumberOfUsersUsingService 回 null、
 *    enableServiceUsageForModels 以 is_null 跳過人數檢查，語意天生相通。
 *  - -1 不可行：DB 兩個欄位皆為 unsigned，且 is_null 判斷不成立會讓
 *    「usersCount > -1」永遠成立 → 任何人被指派都會被擋，效果與需求相反。
 *
 * 為什麼以 sys_alias 判斷而不是寫死 id：
 *  software_services.id 各環境（local/beta/prod）不保證一致，寫死 17/18/19 一旦錯位，
 *  會把別的服務當成「不限人數」送出。sys_alias 是業務唯一鍵，/api/software-services
 *  的每筆 menu item 都有帶。NO_SEAT_SERVICE_IDS 僅作為 menu 尚未載入時的保底。
 * ──────────────────────────────────────────────────────────── */
export const NO_SEAT_SYS_ALIASES = ["data-center", "automation", "blueprint"];
// 保底用（menu 未載入／該筆無 sys_alias 時才會用到）：本地與 seeder 的預設 id
export const NO_SEAT_SERVICE_IDS = [17, 18, 19];
export const isNoSeatService = (id) => NO_SEAT_SERVICE_IDS.includes(id);

/**
 * 是否為無席位服務。優先用 menu item 的 sys_alias（跨環境穩定），
 * 取不到 sys_alias 時才退回寫死的 id 清單。
 * @param {object} menuItem /api/software-services 的該服務項目
 * @param {number} serviceId 服務 id（menu 尚未載入時的保底依據）
 */
export const isNoSeatServiceItem = (menuItem, serviceId) => {
  const alias = menuItem?.sys_alias;
  if (alias) return NO_SEAT_SYS_ALIASES.includes(alias);
  return isNoSeatService(serviceId);
};

/**
 * 無席位服務 → usage-api `enterprise_service_quotas` 的 service key。
 * 前端 sys_alias 用連字號（"data-center"/"automation"），usage-api 用底線
 * （"data_center"/"automation"）。取不到 alias 時以保底 id 對應。回 null＝非年度方案服務。
 */
export const NO_SEAT_USAGE_KEY = {
  "data-center": "data_center",
  automation: "automation",
  blueprint: "blueprint",
};
export const noSeatUsageKey = (menuItem, serviceId) => {
  const alias = menuItem?.sys_alias;
  if (alias) return NO_SEAT_USAGE_KEY[alias] ?? null;
  if (serviceId === 17) return "data_center";
  if (serviceId === 18) return "automation";
  if (serviceId === 19) return "blueprint";
  return null;
};

/**
 * 無席位服務送出用的 plans：不佔席位（users_count = 0），
 * 但仍需帶一筆 plan_id 讓後端決定主方案；無可用方案時回空陣列。
 */
export const buildNoSeatPlans = (plans = []) => {
  const list = Array.isArray(plans) ? plans : [];
  return list.length ? [{ plan_id: list[0].id, users_count: 0 }] : [];
};

/**
 * 單一服務目前的席位數（用於加總「所有服務總席位數」以連動用量管理配額）。
 *  - 無席位服務（NO_SEAT_SERVICE_IDS）不計入席位 → 0
 *  - 席位制服務（SEAT_SERVICE_IDS）取各面額分配人數總和
 *  - 一般服務取 users_count_upper_limit
 */
export const getServiceSeatCount = (data = {}) => {
  const id = data?.software_services_id;
  if (isNoSeatService(id)) return 0;
  if (isSeatService(id)) {
    const allocs = Array.isArray(data?.plan_allocations) ? data.plan_allocations : [];
    return allocs.reduce((sum, a) => sum + (Number(a?.users_count) || 0), 0);
  }
  return Number(data?.users_count_upper_limit) || 0;
};

// 由服務資料 + 面額方案清單建「每方案分配人數」初值；回填既有 plan_allocations，無則 0。
export const toSeatAllocations = (data = {}, plans = []) => {
  const existing = Array.isArray(data?.plan_allocations)
    ? data.plan_allocations
    : [];
  const map = {};
  (Array.isArray(plans) ? plans : []).forEach((p) => {
    const found = existing.find((a) => String(a?.plan_id) === String(p?.id));
    map[p?.id] = Number(found?.users_count) || 0;
  });
  return map;
};

/**
 * 通用「每方案分配人數」初值（所有服務共用，不分訂閱/席位）。
 * 回填既有 plan_allocations；舊資料相容：若無 plan_allocations 明細且僅單一方案，
 * 以 users_count_upper_limit 帶入該方案（避免既有單一席位服務載入變 0）。
 */
export const toPlanAllocations = (data = {}, plans = []) => {
  const list = Array.isArray(plans) ? plans : [];
  const existing = Array.isArray(data?.plan_allocations) ? data.plan_allocations : [];
  const map = {};
  list.forEach((p) => {
    const found = existing.find((a) => String(a?.plan_id) === String(p?.id));
    map[p?.id] = Number(found?.users_count) || 0;
  });
  if (existing.length === 0 && list.length === 1) {
    map[list[0].id] = Number(data?.users_count_upper_limit) || 0;
  }
  return map;
};

// 由「每方案分配人數」建送出用 plans 清單：[{ plan_id, users_count }]。
export const buildPlans = (plans = [], alloc = {}) =>
  (Array.isArray(plans) ? plans : []).map((p) => ({
    plan_id: p?.id,
    users_count: Number(alloc?.[p?.id]) || 0,
  }));

// 分配人數是否有變更（與載入初值相比）。
export const isSeatAllocDirty = (alloc = {}, initial = {}) => {
  const keys = new Set([...Object.keys(alloc), ...Object.keys(initial)]);
  return [...keys].some((k) => (alloc[k] ?? 0) !== (initial[k] ?? 0));
};

// 席位制服務儲存前驗證：分配人數需非負整數；啟用時使用期間必填、總分配需 > 0。
export const validateSeatCard = (form = {}, alloc = {}) => {
  const { activate, start_date, expiration_date } = form;
  const errors = {};
  const values = Object.values(alloc);

  if (values.some((v) => v === "" || !isNonNegativeInteger(Number(v)))) {
    errors.allocations = "分配人數需為 0 或正整數";
  }

  if (
    start_date &&
    expiration_date &&
    new Date(expiration_date) < new Date(start_date)
  ) {
    errors.expiration_date = "結束日期不可早於開始日期";
  }

  if (activate) {
    if (!start_date) errors.start_date = "請填寫開始日期";
    if (!expiration_date) errors.expiration_date = "請填寫結束日期";
    const total = values.reduce((s, v) => s + (Number(v) || 0), 0);
    if (!errors.allocations && total < 1) {
      errors.allocations = "啟用時至少需分配 1 席";
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
};
